<template>
  <div v-if="shouldDisplayGallery">
    <div class="flex justify-center" data-test="carousel">
      <RevCarousel
        :alternative-button-label="i18n(translations.alternativeController)"
        :alternative-next="i18n(translations.alternativeNext)"
        :alternative-previous="i18n(translations.alternativePrevious)"
        class="md:grow"
        current-index-id="gallery-carousel"
        :thumbnails
        :tracking="{ name: 'product', zone: 'pp_iphone17_content_carousel' }"
        @navigation-click="() => trackCarousel('thumbnail_carousel')"
        @next-button-click="() => trackCarousel('right_arrow')"
        @prev-button-click="() => trackCarousel('left_arrow')"
        @swipe="() => trackCarousel('swipe')"
      >
        <RevButtonBase
          v-for="(image, index) in images"
          :key="image.src"
          class="focus-visible-outline-inset-hi rounded-sm relative cursor-pointer"
          @click="handleCarouselClick"
        >
          <RevIllustration
            v-bind="{
              ...(index === 0 && {
                fetchpriority: 'high',
              }),
            }"
            :alt="image.alt"
            class="rounded-lg block !h-[66.7vw] max-h-full w-auto md:!h-auto md:w-full lg:w-[29.125rem]"
            :height="976"
            :loading="shouldLoadEagerly(index)"
            sizes="(max-width: 768px) 100vw, 466px"
            :src="image.src"
            :width="976"
          />
          <div class="absolute bottom-0 left-0 right-0">
            <div
              class="rounded-b-lg absolute bottom-0 left-0 right-0 top-0 bg-[#0a174180] mix-blend-multiply backdrop-blur-lg [mask:linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.1)_10%,rgba(0,0,0,0.5)_32%,rgba(0,0,0,0.8)_50%,rgba(0,0,0,0.9)_69.5%,rgba(0,0,0,1)_100%)]"
            />
            <div
              class="text-action-default-hi mood-inverse body-1-bold relative z-[2] flex items-center px-24 py-24"
            >
              <p>
                {{ image.alt }}
              </p>
            </div>
          </div>
        </RevButtonBase>
      </RevCarousel>
    </div>
    <RevMediaViewer
      :alternative-button-label="i18n(translations.alternativeController)"
      :alternative-next="i18n(translations.alternativeNext)"
      :alternative-previous="i18n(translations.alternativePrevious)"
      :close-button-label="i18n(translations.alternativeClose)"
      current-index-id="gallery-media-viewer"
      data-test="product-page-media-viewer"
      :name="MODAL_NAMES.MEDIA_VIEWER"
      :thumbnails
    >
      <template #default="{ imgClassNames = '' } = {}">
        <RevIllustration
          v-for="(image, index) in images"
          :key="image.alt"
          :alt="image.alt"
          :class="imgClassNames"
          :height="1200"
          :loading="shouldLoadEagerly(index)"
          :src="image.src"
          :width="1200"
        />
      </template>
    </RevMediaViewer>
  </div>
</template>

<script lang="ts" setup>
import { toPublicPath } from '#imports'
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevCarousel } from '@ds/components/Carousel'
import { RevIllustration } from '@ds/components/Illustration'
import { RevMediaViewer } from '@ds/components/MediaViewer'
import { openModal } from '@ds/components/ModalBase'

import { MODAL_NAMES } from '~/scopes/product/constants'

import translations from './Gallery.translations'

const i18n = useI18n()
const { trackClick } = useTracking()

const images = computed(() => {
  return [
    {
      src: toPublicPath(
        '/img/product/iphone-17/Image_carousel_iPhone_17_1.jpg',
      ),
      alt: i18n(translations.imageCarousel1),
    },
    {
      src: toPublicPath(
        '/img/product/iphone-17/Image_carousel_iPhone_17_2.jpg',
      ),
      alt: i18n(translations.imageCarousel2),
    },
    {
      src: toPublicPath(
        '/img/product/iphone-17/Image_carousel_iPhone_17_3.jpg',
      ),
      alt: i18n(translations.imageCarousel3),
    },
    {
      src: toPublicPath(
        '/img/product/iphone-17/Image_carousel_iPhone_17_4.jpg',
      ),
      alt: i18n(translations.imageCarousel4),
    },
    {
      src: toPublicPath(
        '/img/product/iphone-17/Image_carousel_iPhone_17_5.jpg',
      ),
      alt: i18n(translations.imageCarousel5),
    },
  ]
})

const thumbnails = computed(() => {
  return images.value.map((image) => ({
    ...image,
    width: 100,
    height: 100,
  }))
})

const shouldDisplayGallery = computed(() => images.value.length > 0)

function shouldLoadEagerly(index: number) {
  return index === 0 ? 'eager' : 'lazy'
}

function trackCarousel(name: string): void {
  trackClick({
    name,
    zone: 'pp_samsung_s26_content_carousel',
    product_model: 'Samsung s26',
  })
}

function handleCarouselClick(): void {
  trackCarousel('photo_carousel')
  openModal(MODAL_NAMES.MEDIA_VIEWER)
}
</script>
